import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faColumns, faList, faBuilding, faCalendarAlt, faUser, faChevronLeft, faChevronRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';
import KanbanColumn from './components/KanbanColumn';
import ProjectCard from './components/ProjectCard';
import SlotCard from './components/SlotCard';
import VagueBadge from './components/VagueBadge';
import StepTransitionModal from './components/StepTransitionModal';
import TerrainTransitionModal from './components/TerrainTransitionModal';
import StepWarningModal from './components/StepWarningModal';
import ConfirmModal from './components/ConfirmModal';
import ProjectQuickModal from './components/ProjectQuickModal';
import SlotQuickModal from './components/SlotQuickModal';
import { clientShort } from './clientShort';
import { makeKanbanCollisionDetection } from './kanbanCollision';
import './ProjectKanban.css';

const PROJECT_STEPS = ['ao'];
const SLOT_STEPS = ['setup', 'terrain', 'reporting', 'closing'];
const ALL_STEPS = ['ao', 'setup', 'terrain', 'reporting', 'closing'];

const STEP_META = {
  ao:        { label: 'Avant-Vente', color: '#991b1b' },
  setup:     { label: 'Setup',       color: '#d97706' },
  terrain:   { label: 'Terrain',     color: '#059669' },
  reporting: { label: 'Reporting',   color: '#0284c7' },
  closing:   { label: 'Closing',     color: '#7c3aed' },
};

const ProjectKanban = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [data, setData] = useState({
    ao: [], setup: [], terrain: [], reporting: [], closing: [],
  });
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState(null); // { type: 'project'|'slot', data, project? }

  // Vue et filtres vivent dans l'URL, comme /quotas et /frais-deplacement : ouvrir une enquête
  // puis revenir en arrière restaure la recherche au lieu de la perdre.
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const deptFilter = searchParams.get('department') || '';
  const typeFilter = searchParams.get('type') || '';
  const viewMode = searchParams.get('view') || 'board'; // 'board' | 'list' | 'calendar'

  // replace : les filtres ne doivent pas empiler une entrée d'historique par frappe, sinon
  // le bouton Retour reparcourt la saisie lettre par lettre.
  const updateParams = useCallback((next) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      Object.entries(next).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined) p.delete(key);
        else p.set(key, value);
      });
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const setDeptFilter = useCallback((value) => updateParams({ department: value }), [updateParams]);
  const setTypeFilter = useCallback((value) => updateParams({ type: value }), [updateParams]);
  const setViewMode = useCallback((value) => updateParams({ view: value === 'board' ? '' : value }), [updateParams]);

  // Ce qui est tapé reste local ; l'URL — et donc la requête serveur — ne suit qu'après une
  // pause de frappe, là où chaque lettre déclenchait un appel /projects/kanban.
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    if (searchInput === search) return;
    const timer = setTimeout(() => updateParams({ search: searchInput }), 350);
    return () => clearTimeout(timer);
  }, [searchInput, search, updateParams]);

  // Retour arrière ou lien partagé : le champ se resynchronise sur l'URL.
  useEffect(() => { setSearchInput(search); }, [search]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(() => new Date().getDate());

  const [modal, setModal] = useState({ open: false, type: 'basic', project: null, from: null, to: null });
  const [transitioning, setTransitioning] = useState(false);
  const [quickModal, setQuickModal] = useState({ open: false, type: null, project: null, slot: null });
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });

  const collisionDetection = useMemo(() => makeKanbanCollisionDetection(ALL_STEPS), []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  /* ── Fetch ── */
  // Spinner plein écran uniquement au tout premier chargement : sur un changement de filtre,
  // vider le board le temps de la requête donnait un grand blanc entre deux frappes.
  const [refreshing, setRefreshing] = useState(false);

  const fetchKanban = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      else setRefreshing(true);
      const params = {};
      if (search) params.search = search;
      if (deptFilter) params.department = deptFilter;
      if (typeFilter) params.project_type = typeFilter;
      const res = await api.get('/projects/kanban', { params });
      setData(res.data.data);
    } catch (err) {
      console.error('kanban load error', err);
    } finally {
      if (isInitial) setLoading(false);
      else setRefreshing(false);
    }
  }, [search, deptFilter, typeFilter]);

  const firstLoad = useRef(true);

  useEffect(() => {
    fetchKanban(firstLoad.current);
    firstLoad.current = false;
  }, [fetchKanban]);

  // Fetch calendar events when switching to calendar view
  useEffect(() => {
    if (viewMode !== 'calendar') return;
    setCalendarLoading(true);
    api.get('/projects/calendar').then(res => setCalendarEvents(res.data.data || [])).catch(() => {}).finally(() => setCalendarLoading(false));
  }, [viewMode]);

  // Auto-open slot definition modal for newly created Contrat projects
  useEffect(() => {
    const projectId = location.state?.openSlotsForProject;
    if (!projectId || loading) return;
    // Find the project in project_cards (projects without slots)
    const allProjectCards = Object.values(data.project_cards || {}).flat();
    const project = allProjectCards.find(p => p.id === projectId);
    if (project) {
      setModal({ open: true, type: 'terrain', project, from: 'setup', to: 'setup' });
      navigate('/projects', { replace: true, state: {} });
    }
  }, [location.state, loading, data.project_cards, navigate]);

  /* ── Find helpers ── */
  const findProject = (projectId) => {
    for (const step of PROJECT_STEPS) {
      const p = (data[step] || []).find(x => x.id === projectId);
      if (p) return { project: p, step };
    }
    // project_cards is keyed by the project's real step, so use that key. Flattening it
    // and assuming 'setup' made a card shown in Reporting report itself as Setup, which
    // both blocked dragging it to Setup and sent anything dropped onto it to Setup.
    for (const [step, cards] of Object.entries(data.project_cards || {})) {
      const p = (cards || []).find(x => x.id === projectId);
      if (p) return { project: p, step };
    }
    return null;
  };

  const findSlot = (slotId) => {
    for (const step of SLOT_STEPS) {
      for (const group of (data[step] || [])) {
        const s = group.slots.find(x => x.id === slotId);
        if (s) return { slot: s, project: group.project, step };
      }
    }
    return null;
  };

  /* ── DnD ── */
  const onDragStart = ({ active }) => {
    const d = active.data.current;
    if (d?.type === 'slot') {
      setActiveItem({ type: 'slot', data: d.slot, project: d.project });
    } else if (d?.project) {
      setActiveItem({ type: 'project', data: d.project });
    }
  };

  const onDragEnd = ({ active, over }) => {
    setActiveItem(null);
    if (!over) return;

    const activeId = String(active.id);
    let toStep = String(over.id);

    // If dropped on another item, find its column
    if (toStep.startsWith('project-') || toStep.startsWith('slot-')) {
      if (toStep.startsWith('project-')) {
        const tid = parseInt(toStep.replace('project-', ''));
        const found = findProject(tid);
        if (found) toStep = found.step; else return;
      } else {
        const tid = parseInt(toStep.replace('slot-', ''));
        const found = findSlot(tid);
        if (found) toStep = found.step; else return;
      }
    }

    if (!ALL_STEPS.includes(toStep)) return;

    if (activeId.startsWith('slot-')) {
      // Slot card drag — only within terrain/reporting/closing
      const slotId = parseInt(activeId.replace('slot-', ''));
      const found = findSlot(slotId);
      if (!found || found.step === toStep) return;
      if (!SLOT_STEPS.includes(toStep)) {
        if (toStep === 'ao') setErrorModal({ open: true, message: 'Une enquête ne peut pas être déplacée vers l\'étape Avant-Vente' });
        return;
      }

      // Transition slot directly (no modal for slot moves)
      transitionSlot(found, toStep);
    } else if (activeId.startsWith('project-')) {
      // Project card drag
      const projectId = parseInt(activeId.replace('project-', ''));
      const found = findProject(projectId);
      if (!found || found.step === toStep) return;
      if (!ALL_STEPS.includes(toStep)) return;

      setModal({ open: true, type: 'basic', project: found.project, from: found.step, to: toStep });
    }
  };

  /* ── Transitions ── */
  const transitionSlot = async (found, toStep) => {
    // Optimistic move
    setData(prev => {
      const newData = { ...prev };
      // Remove from old step group
      newData[found.step] = (newData[found.step] || []).map(g => {
        if (g.project.id !== found.project.id) return g;
        return { ...g, slots: g.slots.filter(s => s.id !== found.slot.id) };
      }).filter(g => g.slots.length > 0);

      // Add to new step group
      const movedSlot = { ...found.slot, step: toStep };
      const existingGroup = (newData[toStep] || []).find(g => g.project.id === found.project.id);
      if (existingGroup) {
        newData[toStep] = newData[toStep].map(g =>
          g.project.id === found.project.id ? { ...g, slots: [...g.slots, movedSlot] } : g
        );
      } else {
        newData[toStep] = [...(newData[toStep] || []), { project: found.project, slots: [movedSlot] }];
      }
      return newData;
    });

    try {
      await api.post(`/projects/${found.project.id}/slots/${found.slot.id}/transition`, { to_step: toStep });
      fetchKanban(false);
    } catch (err) {
      setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur' });
      fetchKanban(false);
    }
  };

  const closeModal = () => setModal({ open: false, type: 'basic', project: null, from: null, to: null });

  const moveProjectOptimistic = (projectId, fromStep, toStep) => {
    setData(prev => {
      // Search in project arrays (ao, project_cards)
      let project = (prev[fromStep] || []).find(p => p.id === projectId);
      const newData = { ...prev };
      if (project) {
        newData[fromStep] = prev[fromStep].filter(p => p.id !== projectId);
      } else {
        const cards = prev.project_cards || {};
        for (const s of Object.keys(cards)) {
          project = (cards[s] || []).find(p => p.id === projectId);
          if (project) {
            newData.project_cards = { ...cards, [s]: cards[s].filter(p => p.id !== projectId) };
            break;
          }
        }
      }
      if (!project) return prev;

      // Add to target
      if (toStep === 'ao') {
        newData.ao = [project, ...(newData.ao || [])];
      } else {
        const cards = newData.project_cards || {};
        newData.project_cards = { ...cards, [toStep]: [project, ...(cards[toStep] || [])] };
      }
      // For slot columns, the refetch after transition will show correctly
      return newData;
    });
  };

  const confirmTransition = async ({ notes, warnings_acknowledged }) => {
    const { project: proj, from, to } = modal;
    setTransitioning(true);
    try {
      await api.post(`/projects/${proj.id}/transition`, { to_step: to, notes, warnings_acknowledged });
      closeModal();
      if (PROJECT_STEPS.includes(from) && PROJECT_STEPS.includes(to)) {
        moveProjectOptimistic(proj.id, from, to);
      }
      fetchKanban(false);
    } catch (err) { setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur' }); }
    finally { setTransitioning(false); }
  };

  const confirmSetupTransition = async ({ waves, standalone_slots, notes }) => {
    const { project: proj, from, to } = modal;
    setTransitioning(true);
    try {
      // Save slots
      if ((waves?.length > 0) || (standalone_slots?.length > 0)) {
        await api.post(`/projects/${proj.id}/slots`, { waves, standalone_slots });
      }
      // Only transition if not already at the target step (Contrat is already at setup)
      if (from !== to) {
        await api.post(`/projects/${proj.id}/transition`, { to_step: to || 'setup', notes });
      }
      closeModal();
      fetchKanban(false);
    } catch (err) { setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur' }); }
    finally { setTransitioning(false); }
  };

  const totalCount = ALL_STEPS.reduce((s, step) => {
    const d2 = data[step] || [];
    if (!Array.isArray(d2)) return s;
    if (PROJECT_STEPS.includes(step)) return s + d2.length;
    return s + d2.reduce((ss, g) => ss + (g?.slots?.length || 0), 0);
  }, 0) + Object.values(data.project_cards || {}).flat().length;

  // Le compteur ci-dessus additionne des cartes : des projets en Avant-Vente, des enquêtes
  // ensuite. Affiché seul à côté du titre « Projets », il se lisait comme un nombre de projets.
  // On compte donc aussi les projets distincts — calculé sur les données déjà chargées, donc
  // cohérent avec les filtres en cours.
  const projectCount = (() => {
    const ids = new Set();
    (data.ao || []).forEach(p => ids.add(p.id));
    SLOT_STEPS.forEach(step => (data[step] || []).forEach(g => {
      if (g?.project?.id) ids.add(g.project.id);
    }));
    Object.values(data.project_cards || {}).flat().forEach(p => ids.add(p.id));
    return ids.size;
  })();

  /* ── Per-step counts ── */
  const stepCounts = {};
  ALL_STEPS.forEach(step => {
    const d2 = data[step] || [];
    if (PROJECT_STEPS.includes(step)) {
      stepCounts[step] = d2.length;
    } else {
      stepCounts[step] = Array.isArray(d2) ? d2.reduce((s, g) => s + (g?.slots?.length || 0), 0) : 0;
    }
  });
  const projectCards = data.project_cards || {};
  Object.entries(projectCards).forEach(([s, cards]) => {
    stepCounts[s] = (stepCounts[s] || 0) + (cards || []).length;
  });

  /* ── Render ── */
  return (
    <div className="kanban-page">
      {/* Top bar */}
      <div className="kanban-topbar">
        <div className="kanban-topbar-left">
          <h5 className="kanban-topbar-title">Projets</h5>
          {/* Pendant le premier chargement les données sont vides : afficher « 0 projets »
              serait faux. Le board montre déjà son propre spinner à ce moment-là. */}
          {!loading && (
            <span className="kanban-topbar-count"
              title="Projets distincts · cartes affichées (après Avant-Vente, les colonnes affichent les enquêtes)">
              {projectCount} projet{projectCount > 1 ? 's' : ''} · {totalCount} élément{totalCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="kanban-view-toggle">
          <button onClick={() => setViewMode('board')} className={`kanban-view-btn${viewMode === 'board' ? ' active' : ''}`}>
            <FontAwesomeIcon icon={faColumns} style={{ marginRight: 6 }} />Board
          </button>
          <button onClick={() => setViewMode('list')} className={`kanban-view-btn${viewMode === 'list' ? ' active' : ''}`}>
            <FontAwesomeIcon icon={faList} style={{ marginRight: 6 }} />Liste
          </button>
          <button onClick={() => setViewMode('calendar')} className={`kanban-view-btn${viewMode === 'calendar' ? ' active' : ''}`}>
            <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: 6 }} />Calendrier
          </button>
        </div>
        <div className="kanban-topbar-filters">
          <span className="kanban-filter-input-wrap">
            <input className="kanban-filter-input" type="text" placeholder="Rechercher..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') updateParams({ search: e.target.value }); }} />
            {refreshing && (
              <FontAwesomeIcon icon={faSpinner} spin size="sm" className="kanban-filter-spinner"
                title={(search || deptFilter || typeFilter) ? 'Recherche…' : 'Mise à jour…'} />
            )}
          </span>
          <select className="kanban-filter-select" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
            <option value="">Département</option>
            <option value="define">Define</option>
            <option value="voa">Voa</option>
            <option value="immersion">Immersion</option>
          </select>
          <select className="kanban-filter-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">Type</option>
            <option value="contrat">Contrat</option>
            <option value="appel_offre">Appel d'Offre</option>
            <option value="consultation_restreinte">Consultation Restreinte</option>
          </select>
          <button className="kanban-btn-new" onClick={() => navigate('/projects/create')}>+ Nouveau projet</button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center" style={{ flex: 1 }}>
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
        </div>
      ) : viewMode === 'board' ? (
        <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="kanban-board-wrapper">
            <div className="kanban-board">
            {ALL_STEPS.map(step => {
              const meta = STEP_META[step];
              return (
              <div key={step} className="kanban-col" style={{ '--col-accent': meta.color }}>
                {/* Integrated step header */}
                <div className="kanban-step-tab-inline" style={{ borderBottomColor: meta.color, cursor: step === 'closing' ? 'pointer' : 'default' }}
                  onClick={() => step === 'closing' && navigate('/projects/closing')}>
                  <span className="kanban-step-tab-dot" style={{ background: meta.color }} />
                  {meta.label}
                  <span className="kanban-step-tab-count">{stepCounts[step] || 0}</span>
                </div>
              <KanbanColumn
                step={step}
                hideHeader
                projects={PROJECT_STEPS.includes(step) ? (data[step] || []) : ((data.project_cards || {})[step] || [])}
                slotGroups={SLOT_STEPS.includes(step) ? (data[step] || []) : []}
                onCardClick={projectId => {
                  const p = (data.ao || []).find(x => x.id === projectId) ||
                            Object.values(data.project_cards || {}).flat().find(x => x.id === projectId);
                  if (p) setQuickModal({ open: true, type: 'project', project: p, slot: null });
                  else {
                    for (const s of SLOT_STEPS) {
                      const grp = (data[s] || []).find(g => g.project?.id === projectId);
                      if (grp) { setQuickModal({ open: true, type: 'project', project: grp.project, slot: null }); break; }
                    }
                  }
                }}
                onSlotClick={(slot, project) => setQuickModal({ open: true, type: 'slot', project, slot })}
              />
              </div>
              );
            })}
          </div>
          </div>

          <DragOverlay dropAnimation={null}>
            {activeItem?.type === 'project' && (
              <div style={{ width: '260px' }}>
                <ProjectCard project={activeItem.data} onClick={() => {}} isDragOverlay />
              </div>
            )}
            {activeItem?.type === 'slot' && (
              <div style={{ width: '240px' }}>
                <SlotCard slot={activeItem.data} project={activeItem.project} onClick={() => {}} isDragOverlay />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      ) : viewMode === 'list' ? (
        /* ── List view ── */
        <div className="kanban-list-view">
        <div className="kanban-list-inner">
          {/* Table header */}
          <div className="kanban-list-thead">
            <span className="kanban-list-th" style={{ flex: 2.5 }}>Nom</span>
            <span className="kanban-list-th" style={{ flex: 0.8 }}>Département</span>
            <span className="kanban-list-th" style={{ flex: 0.8 }}>Type</span>
            <span className="kanban-list-th" style={{ flex: 1.3 }}>Client</span>
            <span className="kanban-list-th" style={{ flex: 1.3 }}>Assigné(s)</span>
            <span className="kanban-list-th" style={{ flex: 0.8, textAlign: 'right' }}>Échéance</span>
          </div>

          {ALL_STEPS.map(step => {
            const meta = STEP_META[step];
            const projects = PROJECT_STEPS.includes(step) ? (data[step] || []) : ((data.project_cards || {})[step] || []);
            const groups = SLOT_STEPS.includes(step) ? (data[step] || []) : [];
            const hasContent = projects.length > 0 || groups.length > 0;
            if (!hasContent) return null;

            const DEPT = { define: { bg: '#e8f0fe', color: '#1967d2', label: 'Define' }, voa: { bg: '#fef7cd', color: '#a16207', label: 'Voa' }, immersion: { bg: '#fce8e6', color: '#c5221f', label: 'Immersion' } };

            return (
              <div key={step} className="kanban-list-section">
                {/* Step group header */}
                <div className="kanban-list-section-header">
                  <span className="kanban-list-section-dot" style={{ background: meta.color }} />
                  <span className="kanban-list-section-title" style={step === 'closing' ? { cursor: 'pointer' } : {}} onClick={() => step === 'closing' && navigate('/projects/closing')}>{meta.label}</span>
                  <span className="kanban-list-section-count">{stepCounts[step] || 0}</span>
                </div>

                <div className="kanban-list-section-body">
                  {/* Project rows */}
                  {projects.map(p => {
                    const dept = DEPT[p.department] || DEPT.immersion;
                    const dl = p[`${p.step}_deadline`];
                    const dlObj = dl ? new Date(dl) : null;
                    const dlHasTime = dlObj && p.step === 'ao' && (dlObj.getHours() !== 0 || dlObj.getMinutes() !== 0);
                    const dlDate = dlObj ? (dlObj.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) + (dlHasTime ? ' · ' + dlObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '')) : null;
                    const dlDiff = dl ? Math.ceil((new Date(dl) - new Date().setHours(0,0,0,0)) / 86400000) : null;
                    const dlCls = dlDiff !== null ? (dlDiff < 0 ? 'deadline-overdue' : dlDiff <= 3 ? 'deadline-soon' : '') : '';
                    const assignees = (p.assignees?.length > 0) ? p.assignees.map(a => a.name).join(', ') : (p.assigned_to?.name || '');
                    return (
                      <div key={p.id} className="kanban-list-row" onClick={() => setQuickModal({ open: true, type: 'project', project: p, slot: null })}>
                        <span className="kanban-list-row-name" style={{ flex: 2.5 }}>
                          <span className="kanban-list-row-accent" style={{ background: meta.color }} />
                          {p.client?.name && <span style={{ color: '#9e9e9e', fontWeight: 600, marginRight: 4 }}>{clientShort(p.client.name, p.client.abbreviation)} -</span>}{p.name}
                        </span>
                        <span style={{ flex: 0.8 }}>
                          <span className="badge-dept" style={{ background: dept.bg, color: dept.color, fontSize: '0.58rem' }}>{dept.label}</span>
                        </span>
                        <span style={{ flex: 0.8 }}>
                          <span className="badge-type" style={{ fontSize: '0.58rem' }}>{{ contrat: 'Contrat', appel_offre: 'AO', consultation_restreinte: 'CR' }[p.project_type] || p.project_type}</span>
                        </span>
                        <span className="kanban-list-row-client" style={{ flex: 1.3 }}>
                          {p.client?.name && <><FontAwesomeIcon icon={faBuilding} size="xs" className="kanban-list-icon" />{p.client.name}</>}
                        </span>
                        <span className="kanban-list-row-assignee" style={{ flex: 1.3 }}>
                          {assignees && <><FontAwesomeIcon icon={faUser} size="xs" className="kanban-list-icon" />{assignees}</>}
                        </span>
                        <span className={`kanban-list-row-deadline ${dlCls}`} style={{ flex: 0.8 }}>
                          {dlDate && <><FontAwesomeIcon icon={faCalendarAlt} size="xs" className="kanban-list-icon" />{dlDate}</>}
                        </span>
                      </div>
                    );
                  })}

                  {/* Slot rows grouped by project */}
                  {groups.map(group => {
                    const groupDept = DEPT[group.project.department] || DEPT.immersion;
                    const groupType = { contrat: 'Contrat', appel_offre: 'AO', consultation_restreinte: 'CR' }[group.project.project_type] || group.project.project_type;
                    return (
                      <React.Fragment key={group.project.id}>
                        {group.slots.map(slot => {
                          const isCati = slot.survey && slot.survey.poste_qcode !== 'ref';
                          const assignees = (slot.assignees?.length > 0) ? slot.assignees.map(a => a.name).join(', ') : '';
                          const slotDateFin = slot.survey?.date_fin;
                          const slotDlDate = slotDateFin ? new Date(slotDateFin).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : null;
                          const slotDlDiff = slotDateFin ? Math.ceil((new Date(slotDateFin) - new Date().setHours(0,0,0,0)) / 86400000) : null;
                          const slotDlCls = slotDlDiff !== null ? (slotDlDiff < 0 ? 'deadline-overdue' : slotDlDiff <= 3 ? 'deadline-soon' : '') : '';
                          return (
                            <div key={slot.id} className="kanban-list-row kanban-list-row-slot" onClick={() => setQuickModal({ open: true, type: 'slot', project: group.project, slot })}>
                              <span className="kanban-list-row-name" style={{ flex: 2.5 }}>
                                <span className="kanban-list-row-accent" style={{ background: meta.color }} />
                                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                  <span style={{ fontSize: '0.68rem', color: '#9e9e9e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                                    {group.project.name}
                                  </span>
                                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#242424', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                                    {group.project.client?.name && <span style={{ color: '#9e9e9e', fontWeight: 600, marginRight: 4 }}>{clientShort(group.project.client.name, group.project.client.abbreviation)} -</span>}{slot.name}
                                  </span>
                                </span>
                              </span>
                              <span style={{ flex: 0.8 }}>
                                <span className="badge-dept" style={{ background: groupDept.bg, color: groupDept.color, fontSize: '0.55rem' }}>{groupDept.label}</span>
                              </span>
                              <span style={{ flex: 0.8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                <span className="badge-type" style={{ fontSize: '0.55rem' }}>{groupType}</span>
                                {slot.survey && (
                                  <span style={{ fontSize: '0.55rem', fontWeight: 600, padding: '2px 6px', borderRadius: 3, background: isCati ? '#f3e8ff' : '#dbeafe', color: isCati ? '#7c3aed' : '#0284c7' }}>
                                    {isCati ? 'CATI' : 'F2F'}
                                  </span>
                                )}
                                <VagueBadge wave={slot.survey_wave} />
                              </span>
                              <span className="kanban-list-row-client" style={{ flex: 1.3 }}>
                                {group.project.client?.name && <><FontAwesomeIcon icon={faBuilding} size="xs" className="kanban-list-icon" />{group.project.client.name}</>}
                              </span>
                              <span className="kanban-list-row-assignee" style={{ flex: 1.3 }}>
                                {assignees && <><FontAwesomeIcon icon={faUser} size="xs" className="kanban-list-icon" />{assignees}</>}
                              </span>
                              <span className={`kanban-list-row-deadline ${slotDlCls}`} style={{ flex: 0.8 }}>
                                {slotDlDate ? (
                                  <><FontAwesomeIcon icon={faCalendarAlt} size="xs" className="kanban-list-icon" />{slotDlDate}</>
                                ) : slot.is_finished ? (
                                  <span style={{ fontWeight: 600, color: '#137333', background: '#e6f4ea', padding: '2px 8px', borderRadius: 3, fontSize: '0.6rem' }}>Terminée</span>
                                ) : (
                                  <span style={{ color: '#bdbdbd', fontSize: '0.6rem' }}>—</span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      ) : viewMode === 'calendar' ? (
        /* ── Calendar view ── */
        (() => {
          const year = calendarMonth.getFullYear();
          const month = calendarMonth.getMonth();
          const firstDay = new Date(year, month, 1);
          const lastDay = new Date(year, month + 1, 0);
          const startDow = (firstDay.getDay() + 6) % 7; // Monday = 0
          const daysInMonth = lastDay.getDate();
          const today = new Date();
          const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

          // Group events by date
          const eventsByDate = {};
          calendarEvents.forEach(ev => {
            if (!eventsByDate[ev.date]) eventsByDate[ev.date] = [];
            eventsByDate[ev.date].push(ev);
          });

          // Build grid cells
          const cells = [];
          for (let i = 0; i < startDow; i++) cells.push(null);
          for (let d = 1; d <= daysInMonth; d++) cells.push(d);
          while (cells.length % 7 !== 0) cells.push(null);

          const monthLabel = firstDay.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
          const prevMonth = () => {
            const pm = new Date(year, month - 1, 1);
            setCalendarMonth(pm);
            const now = new Date();
            setSelectedDay(pm.getFullYear() === now.getFullYear() && pm.getMonth() === now.getMonth() ? now.getDate() : 1);
          };
          const nextMonth = () => {
            const nm = new Date(year, month + 1, 1);
            setCalendarMonth(nm);
            const now = new Date();
            setSelectedDay(nm.getFullYear() === now.getFullYear() && nm.getMonth() === now.getMonth() ? now.getDate() : 1);
          };

          const selectedDateStr = selectedDay ? `${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}` : null;
          const selectedEvents = selectedDateStr ? (eventsByDate[selectedDateStr] || []) : [];

          return (
            <div className="kanban-calendar-view">
              {calendarLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
                </div>
              ) : (
                <div className="kanban-cal-layout">
                  {/* Calendar grid */}
                  <div className="kanban-cal-main">
                    {/* Month nav */}
                    <div className="kanban-cal-nav">
                      <button onClick={prevMonth} className="kanban-cal-nav-btn"><FontAwesomeIcon icon={faChevronLeft} /></button>
                      <span className="kanban-cal-month">{monthLabel}</span>
                      <button onClick={nextMonth} className="kanban-cal-nav-btn"><FontAwesomeIcon icon={faChevronRight} /></button>
                    </div>

                    {/* Day headers */}
                    <div className="kanban-cal-grid kanban-cal-header-row">
                      {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
                        <div key={d} className="kanban-cal-day-header">{d}</div>
                      ))}
                    </div>

                    {/* Day cells */}
                    <div className="kanban-cal-grid kanban-cal-body">
                      {cells.map((day, i) => {
                        if (day === null) return <div key={`empty-${i}`} className="kanban-cal-cell kanban-cal-cell-empty" />;
                        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const dayEvents = eventsByDate[dateStr] || [];
                        const isToday = dateStr === todayStr;
                        const isSelected = day === selectedDay;
                        const isWeekend = (i % 7) >= 5;

                        return (
                          <div
                            key={day}
                            className={`kanban-cal-cell${isToday ? ' kanban-cal-today' : ''}${isSelected ? ' kanban-cal-selected' : ''}${isWeekend ? ' kanban-cal-weekend' : ''}`}
                            onClick={() => setSelectedDay(isSelected ? null : day)}
                          >
                            <span className="kanban-cal-day-num">{day}</span>
                            {dayEvents.length > 0 && (
                              <div className="kanban-cal-dots">
                                {dayEvents.slice(0, 4).map((ev, j) => (
                                  <span key={j} className="kanban-cal-dot" style={{ background: ev.color }} title={ev.label + ' — ' + (ev.client ? clientShort(ev.client, ev.client_abbreviation) + ' - ' : '') + ev.project} />
                                ))}
                                {dayEvents.length > 4 && <span className="kanban-cal-dot-more">+{dayEvents.length - 4}</span>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Side panel: selected day events */}
                  <div className="kanban-cal-side">
                    {selectedDay ? (
                      <>
                        <div className="kanban-cal-side-header">
                          {new Date(year, month, selectedDay).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </div>
                        {selectedEvents.length === 0 ? (
                          <div className="kanban-cal-side-empty">Aucun événement</div>
                        ) : (
                          <div className="kanban-cal-side-list">
                            {selectedEvents.map((ev, i) => (
                              <div key={i} className="kanban-cal-event" onClick={() => navigate(`/projects/${ev.project_id}`)}>
                                <span className="kanban-cal-event-dot" style={{ background: ev.color }} />
                                <div className="kanban-cal-event-content">
                                  <div className="kanban-cal-event-label">{ev.label}</div>
                                  <div className="kanban-cal-event-meta">
                                    {ev.client && <span style={{ color: '#9e9e9e', fontWeight: 600 }}>{clientShort(ev.client, ev.client_abbreviation)} - </span>}{ev.project}
                                    {ev.slot && <> · {ev.slot}</>}
                                    {ev.survey && <> · <span style={{ color: '#9e9e9e' }}>{ev.survey}</span></>}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="kanban-cal-side-empty">
                        <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: '1.5rem', color: '#e0e0e0', marginBottom: 8 }} />
                        <div>Sélectionnez un jour</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()
      ) : null}

      {modal.type === 'basic' && (
        <StepTransitionModal isOpen={modal.open} onClose={closeModal} onConfirm={confirmTransition}
          project={modal.project} fromStep={modal.from} toStep={modal.to} loading={transitioning} />
      )}
      {modal.type === 'terrain' && (
        <TerrainTransitionModal isOpen={modal.open} onClose={closeModal} onConfirm={confirmSetupTransition}
          project={modal.project} loading={transitioning} />
      )}
      {modal.type === 'warning' && (
        <StepWarningModal isOpen={modal.open} onClose={closeModal} onConfirm={confirmTransition}
          project={modal.project} fromStep={modal.from} toStep={modal.to} loading={transitioning} />
      )}

      <ProjectQuickModal
        isOpen={quickModal.open && quickModal.type === 'project'}
        onClose={() => { setQuickModal({ open: false, type: null, project: null, slot: null }); fetchKanban(false); }}
        project={quickModal.project}
        onUpdate={() => fetchKanban(false)}
      />
      <SlotQuickModal
        isOpen={quickModal.open && quickModal.type === 'slot'}
        onClose={() => { setQuickModal({ open: false, type: null, project: null, slot: null }); fetchKanban(false); }}
        slot={quickModal.slot}
        project={quickModal.project}
        onUpdate={() => fetchKanban(false)}
      />

      <ConfirmModal
        isOpen={errorModal.open}
        onClose={() => setErrorModal({ open: false, message: '' })}
        title="Erreur"
        message={errorModal.message}
        variant="danger"
        dismissOnBackdrop={false}
      />
    </div>
  );
};

export default ProjectKanban;
